/**
 * Konstans dla notyfikacji — grupowanie typów, role mapping, defaults.
 *
 * Architektura per-user prefs:
 * 1. user.notificationPrefs = Record<NotificationType, boolean> | null
 * 2. NULL = fallback do DEFAULT_PREFS_BY_ROLE
 * 3. Per-typ override w prefs (np. handlowiec wyłączył lead_edited_by_other)
 * 4. `system` zawsze enabled — admin broadcasts (maintenance, new features)
 *
 * Klient (handlowiec): "dostaję za dużo powiadomień, zwłaszcza ze admin
 * cos zmienil w kontakcie - chce wylaczyc". Stąd lead_edited_by_other
 * DOMYŚLNIE WYŁĄCZONE dla handlowca.
 */

import type { NotificationType } from '../types/notification.js';
import type { UserRole } from './roles.js';

/**
 * Grupy notyfikacji do UI accordion — 6 sekcji.
 *
 * Każda grupa ma:
 * - label (PL do header)
 * - icon (lucide-react name string — UI mapuje na komponent)
 * - description (tooltip / podtytuł)
 * - types[] z opisami "co triggeruje"
 */
export const NOTIFICATION_GROUPS: Array<{
  id: string;
  label: string;
  icon: string;
  description: string;
  types: Array<{
    type: NotificationType;
    label: string;
    description: string;
  }>;
}> = [
  {
    id: 'reminders',
    label: 'Przypomnienia',
    icon: 'Clock',
    description: 'Przypomnienia o terminach — kontakt, spotkanie, oferta',
    types: [
      {
        type: 'reminder_contact',
        label: 'Kontakt z klientem',
        description: 'Czas zadzwonić — termin ustawiony przez handlowca',
      },
      {
        type: 'reminder_meeting',
        label: 'Spotkanie',
        description: 'Czas pojechać na spotkanie z klientem',
      },
      {
        type: 'reminder_offer',
        label: 'Kontakt po ofercie',
        description: '2 dni robocze po wysyłce — dopytaj klienta, co sądzi o ofercie',
      },
      {
        type: 'reminder_send_offer',
        label: 'Wyślij ofertę',
        description: 'Czas wysłać ofertę — termin ustawiony ręcznie',
      },
    ],
  },
  {
    id: 'email',
    label: 'Śledzenie maili',
    icon: 'Mail',
    description: 'Co się dzieje z mailem z ofertą — otwarcie, kliknięcie, mail nie dotarł',
    types: [
      {
        type: 'offer_opened',
        label: 'Mail z ofertą otwarty',
        description:
          'Mail z ofertą został otwarty (programy pocztowe często otwierają maile same, więc to nie znaczy, że klient widział cenę)',
      },
      {
        type: 'offer_clicked',
        label: 'Klient obejrzał ofertę',
        description: 'Klient kliknął link w mailu i otworzył ofertę z ceną',
      },
      {
        type: 'offer_bounced',
        label: 'Mail nie dotarł ⚠️',
        description:
          'Serwer klienta odrzucił mail — literówka w adresie, pełna skrzynka lub blokada',
      },
      {
        type: 'offer_reaction',
        label: 'Klient odpowiedział na ofertę',
        description:
          'Klient kliknął w mailu przycisk z pytaniem, prośbą o negocjacje albo rezygnacją',
      },
    ],
  },
  {
    id: 'lead_lifecycle',
    label: 'Cykl życia leada',
    icon: 'UserCog',
    description: 'Przypisania, cofnięcia, ponowne zgłoszenia',
    types: [
      {
        type: 'lead_unassigned',
        label: 'Lead odebrany',
        description: 'Admin/dyrektor zabrał ci leada (przeniesiono do koszyka)',
      },
      {
        type: 'lead_resubmitted',
        label: 'Lead wrócił',
        description: 'Klient zgłosił się ponownie (z formularza na stronie)',
      },
    ],
  },
  {
    id: 'lead_activity',
    label: 'Aktywność na leadach',
    icon: 'Edit',
    description: 'Notatki i zmiany w leadach od innych osób',
    types: [
      {
        type: 'lead_note_by_other',
        label: 'Ktoś dodał notatkę',
        description: 'Marketing/admin dopisał notatkę do twojego leada',
      },
      {
        type: 'lead_edited_by_other',
        label: 'Ktoś edytował pole',
        description: 'Admin/marketing zmienił dane kontaktowe (np. poprawił literówkę)',
      },
    ],
  },
  {
    id: 'discount',
    label: 'Rabaty',
    icon: 'Percent',
    description: 'Prośby o rabat i decyzje admina',
    types: [
      {
        type: 'discount_request_pending',
        label: 'Prośba o rabat',
        description: 'Handlowiec poprosił o rabat — wymaga twojej decyzji',
      },
      {
        type: 'discount_request_resolved',
        label: 'Decyzja w sprawie rabatu',
        description: 'Admin/dyrektor zaakceptował lub odrzucił twoją prośbę',
      },
    ],
  },
  {
    id: 'offer_deletion',
    label: 'Usuwanie ofert',
    icon: 'Trash2',
    description: 'Prośby o usunięcie ofert i decyzje admina',
    types: [
      {
        type: 'offer_deletion_request_pending',
        label: 'Prośba o usunięcie oferty',
        description: 'Handlowiec poprosił o usunięcie oferty — wymaga twojej decyzji',
      },
      {
        type: 'offer_deletion_request_resolved',
        label: 'Decyzja usunięcia',
        description: 'Admin zaakceptował lub odrzucił twoją prośbę o usunięcie',
      },
    ],
  },
  {
    id: 'note_annotations',
    label: 'Adnotacje do notatek',
    icon: 'MessageCircle',
    description: 'Sprostowania do twoich notatek',
    types: [
      {
        type: 'lead_note_annotated',
        label: 'Ktoś dodał sprostowanie',
        description: 'Do notatki dopisano sprostowanie lub komentarz',
      },
    ],
  },
  {
    id: 'projects',
    label: 'Projekty',
    icon: 'Building2',
    description: 'Nowe projekty, zespół, status, dokumenty',
    types: [
      {
        type: 'project_created',
        label: 'Nowy projekt',
        description: 'Handlowiec zamienił ofertę na projekt — jesteś jego kierownikiem',
      },
      {
        type: 'project_assignment_added',
        label: 'Dodano cię do zespołu',
        description:
          'Kierownik projektu dodał cię jako kierownika budowy, inżyniera lub zaopatrzeniowca',
      },
      {
        type: 'project_assignment_removed',
        label: 'Usunięto cię z zespołu',
        description: 'Kierownik projektu usunął cię z projektu',
      },
      {
        type: 'project_manager_changed',
        label: 'Zmiana kierownika projektu',
        description: 'Admin/dyrektor wyznaczył cię na kierownika projektu',
      },
      {
        type: 'project_salesperson_changed',
        label: 'Zmiana opieki handlowej',
        description: 'Admin przekazał ci opiekę nad projektem',
      },
      {
        type: 'project_status_changed',
        label: 'Zmiana statusu',
        description: 'Zmienił się status projektu, który prowadzisz',
      },
      {
        type: 'project_note_mention',
        label: 'Wzmianka o tobie w notatce',
        description: 'Ktoś oznaczył cię (@TwojeImię) w notatce projektu',
      },
      {
        type: 'project_cancelled',
        label: 'Projekt anulowany',
        description: 'Kierownik projektu lub admin anulował projekt z podanym powodem',
      },
      {
        type: 'project_document_uploaded',
        label: 'Nowy dokument',
        description: 'Ktoś wgrał plik (umowa/projekt/faktura) do projektu',
      },
    ],
  },
  {
    id: 'feedback',
    label: 'Uwagi testerów',
    icon: 'MessageSquare',
    description: 'Wątki Dawid ↔ użytkownicy',
    types: [
      {
        type: 'feedback_replied',
        label: 'Odpowiedź Dawida',
        description: 'Dawid odpowiedział na twoją uwagę / zgłoszenie',
      },
      {
        type: 'feedback_tester_replied',
        label: 'Tester dopisał',
        description: 'Autor uwagi dopisał coś w wątku (widzi tylko twórca aplikacji)',
      },
    ],
  },
];

/**
 * Które typy notyfikacji może otrzymać dana rola. Niewymienione typy =
 * rola nigdy ich nie dostaje (backend nigdy nie createNotification z tym
 * type dla tej roli).
 *
 * UI używa tej mapy do UKRYCIA grup/typów których user nie może otrzymać
 * (np. handlowiec nie widzi `discount_request_pending` bo to admin-only).
 */
export const ROLE_RECEIVABLE_TYPES: Record<UserRole, ReadonlySet<NotificationType>> = {
  handlowiec: new Set<NotificationType>([
    'reminder_contact',
    'reminder_meeting',
    'reminder_offer',
    'reminder_send_offer',
    'offer_opened',
    'offer_clicked',
    'offer_bounced',
    'offer_reaction',
    'lead_unassigned',
    // Klient wlasnego leada wypelnil formularz ponownie (Dawid 2026-10-01: „do zrobienia”).
    'lead_resubmitted',
    'lead_note_by_other',
    'lead_edited_by_other',
    'lead_note_annotated',
    'discount_request_resolved',
    'offer_deletion_request_resolved',
    'feedback_replied',
    // Faza 2a — handlowiec opiekun projektu dostaje wszystkie eventy projektu
    'project_created',
    'project_assignment_added',
    'project_assignment_removed',
    'project_manager_changed',
    'project_salesperson_changed',
    'project_status_changed',
    'project_note_mention',
    'project_cancelled',
    'project_document_uploaded',
    'system',
  ]),
  marketing: new Set<NotificationType>(['lead_resubmitted', 'feedback_replied', 'system']),
  dyrektor_sprzedazy: new Set<NotificationType>([
    // Też reminders bo dyrektor może być handlowcem
    'reminder_contact',
    'reminder_meeting',
    'reminder_offer',
    'reminder_send_offer',
    'offer_opened',
    'offer_clicked',
    'offer_bounced',
    'offer_reaction',
    'lead_unassigned',
    'lead_resubmitted',
    'lead_note_annotated',
    'discount_request_pending',
    'discount_request_resolved',
    // Dyrektor VIEW prosby o usuniecie (ale NIE moze decydowac — tylko admin/dev)
    'offer_deletion_request_pending',
    'feedback_replied',
    // Faza 2a — dyr widzi wszystkie eventy projektowe (nadzor)
    'project_created',
    'project_assignment_added',
    'project_assignment_removed',
    'project_manager_changed',
    'project_salesperson_changed',
    'project_status_changed',
    'project_note_mention',
    'project_cancelled',
    'project_document_uploaded',
    'system',
  ]),
  admin: new Set<NotificationType>([
    'reminder_contact',
    'reminder_meeting',
    'reminder_offer',
    'reminder_send_offer',
    'offer_opened',
    'offer_clicked',
    'offer_bounced',
    'offer_reaction',
    'lead_unassigned',
    'lead_resubmitted',
    'lead_note_annotated',
    'discount_request_pending',
    'discount_request_resolved',
    'offer_deletion_request_pending',
    'offer_deletion_request_resolved',
    'feedback_replied',
    // Faza 2a — admin nadzoruje wszystkie projekty
    'project_created',
    'project_assignment_added',
    'project_assignment_removed',
    'project_manager_changed',
    'project_salesperson_changed',
    'project_status_changed',
    'project_note_mention',
    'project_cancelled',
    'project_document_uploaded',
    'system',
  ]),
  developer: new Set<NotificationType>([
    'reminder_contact',
    'reminder_meeting',
    'reminder_offer',
    'reminder_send_offer',
    'offer_opened',
    'offer_clicked',
    'offer_bounced',
    'offer_reaction',
    'lead_unassigned',
    'lead_resubmitted',
    'lead_note_annotated',
    'discount_request_pending',
    'discount_request_resolved',
    'offer_deletion_request_pending',
    'offer_deletion_request_resolved',
    'feedback_replied',
    'feedback_tester_replied', // tylko dla developer
    // Faza 2a — dev/admin nadzoruje projekty
    'project_created',
    'project_assignment_added',
    'project_assignment_removed',
    'project_manager_changed',
    'project_salesperson_changed',
    'project_status_changed',
    'project_note_mention',
    'project_cancelled',
    'project_document_uploaded',
    'system',
  ]),
  // Faza 2a — kierownik/inzynier dostaja project_* (sa w assignments)
  kierownik: new Set<NotificationType>([
    'project_assignment_added',
    'project_assignment_removed',
    'project_status_changed',
    'project_note_mention',
    'project_cancelled',
    'project_document_uploaded',
    'system',
  ]),
  inzynier: new Set<NotificationType>([
    'project_assignment_added',
    'project_assignment_removed',
    'project_status_changed',
    'project_note_mention',
    'project_cancelled',
    'project_document_uploaded',
    'system',
  ]),
  // Zaopatrzenie — Faza 5, na razie tylko system
  zaopatrzenie: new Set<NotificationType>(['system']),
  klient: new Set<NotificationType>(['system']),
};

/**
 * Domyślne preferences per rola. Stosowane gdy user.notificationPrefs jest NULL
 * (np. user nie wszedł nigdy na /notyfikacje/ustawienia).
 *
 * Konwencja:
 * - Wszystkie receivable typy = true (włączone)
 * - Wyjątek: handlowiec lead_edited_by_other = false (zgłaszał spam admin'a)
 * - `system` zawsze true (forced, nie pokazujemy switcha w UI)
 */
export const DEFAULT_PREFS_BY_ROLE: Record<UserRole, Record<NotificationType, boolean>> = (() => {
  const result = {} as Record<UserRole, Record<NotificationType, boolean>>;
  const allRoles: UserRole[] = [
    'handlowiec',
    'marketing',
    'dyrektor_sprzedazy',
    'admin',
    'developer',
    'kierownik',
    'inzynier',
    'zaopatrzenie',
    'klient',
  ];

  for (const role of allRoles) {
    const prefs = {} as Record<NotificationType, boolean>;
    for (const type of ROLE_RECEIVABLE_TYPES[role]) {
      prefs[type] = true; // wszystko enabled by default
    }
    result[role] = prefs;
  }

  // Override: handlowiec — wyłącz lead_edited_by_other (spam admin edits)
  result.handlowiec.lead_edited_by_other = false;

  return result;
})();

/**
 * Czy dany typ jest WYMUSZONY (user nie może wyciszyć). Aktualnie: tylko `system`.
 * Backend `isNotificationEnabled` ignoruje prefs[type] dla wymuszonych.
 */
export const FORCED_NOTIFICATION_TYPES: ReadonlySet<NotificationType> = new Set(['system']);

/**
 * Typy notyfikacji ktore wymagaja AKCJI W LEADZIE od handlowca (3-stan workflow):
 *   NEW -> klik = TODO (readAt=now, zostaje w bell counter "do akcji")
 *   TODO -> akcja w leadzie (notatka/status/deadline) lub manual = RESOLVED
 *
 * Klient (handlowiec 2026-05-14): "powiadomienia typu rabaty, koszyki to nie
 * maja zastosowania do tego. Chodzi mi tylko o powiadomienia dotyczace
 * kontaktow handlowca: przypomnienie o wycenie, przypomnienie o kontakcie itd."
 *
 * Wiktor (handlowiec 2026-05-15): "klient otwiera maila 15.05, 16.05, 17.05
 * i sa 3 te same powiadomienia. Chce zeby klik kasowal je od razu." -> wywalono
 * offer_opened i offer_clicked z actionable (jako info-only "klient zyje").
 * Workflow handlowca: reminder_offer cron +2 dni daje cue do telefonu, NIE
 * pojedyncze otwarcia maila.
 *
 * Pozostale typy (lead_unassigned, lead_resubmitted, discount_request_*,
 * feedback_*, lead_note_by_other, lead_edited_by_other, offer_opened/clicked,
 * system) sa INFO-ONLY:
 *   NEW -> klik = od razu RESOLVED (readAt + resolvedAt = now)
 *   Brak stanu "TODO" — nic do robienia w leadzie.
 *
 * 5 typow actionable:
 * - reminder_contact / meeting / offer / send_offer (4 typy reminders)
 * - offer_bounced (KRYTYCZNE — fix email, NIE info-only)
 */
export const ACTIONABLE_NOTIFICATION_TYPES: ReadonlySet<NotificationType> = new Set([
  'reminder_contact',
  'reminder_meeting',
  'reminder_offer',
  'reminder_send_offer',
  'offer_bounced',
  // Reakcja klienta na oferte (mig 0075) — w przeciwienstwie do offer_opened
  // to swiadoma decyzja czlowieka (dwuetapowe potwierdzenie). Wymaga telefonu,
  // wiec ma byc todo-itemem, nie info-only.
  'offer_reaction',
]);

/**
 * Czy powiadomienie jest „do zrobienia” dla tej roli. Ponowne zgłoszenie klienta
 * jest zadaniem dla handlowca-właściciela (Dawid 2026-10-01); dla adminów i marketingu
 * (zgłoszenie z koszyka, wysyłane do wielu osób) zostaje informacją.
 */
export function isActionableNotification(type: NotificationType, role: UserRole | null): boolean {
  if (ACTIONABLE_NOTIFICATION_TYPES.has(type)) return true;
  return type === 'lead_resubmitted' && role === 'handlowiec';
}

/**
 * Sprawdz czy dany typ notyfikacji jest enabled dla usera. Logic:
 * 1. Forced (system) → zawsze true
 * 2. Niereceivable dla roli → zawsze false (rola nigdy tego nie dostaje)
 * 3. user.prefs[type] zdefiniowane → użyj
 * 4. user.prefs == null lub bez klucza → fallback do DEFAULT_PREFS_BY_ROLE[role][type]
 * 5. Default jeśli też brak (np. forward compat nowy typ) → true (enabled)
 */
export function isNotificationEnabled(
  userRole: UserRole,
  userPrefs: Record<string, boolean> | null | undefined,
  type: NotificationType,
): boolean {
  if (FORCED_NOTIFICATION_TYPES.has(type)) return true;
  if (!ROLE_RECEIVABLE_TYPES[userRole].has(type)) return false;
  if (userPrefs && type in userPrefs) return userPrefs[type] === true;
  const roleDefaults = DEFAULT_PREFS_BY_ROLE[userRole];
  if (type in roleDefaults) return roleDefaults[type] === true;
  return true; // forward compat: nowy typ nie w defaults = enabled
}

/**
 * Etykiety kart klienta (Dawid 2026-10-01): kilka kolorów, nazwy ustawia sam handlowiec.
 * Domyślne nazwy — punkt startu, do zmiany w Ustawieniach powiadomień.
 */
export const NOTIFICATION_LABEL_COLORS = ['red', 'amber', 'green', 'blue', 'violet'] as const;
export type NotificationLabelColor = (typeof NOTIFICATION_LABEL_COLORS)[number];
export const DEFAULT_NOTIFICATION_LABEL_NAMES: Record<NotificationLabelColor, string> = {
  red: 'Pilne',
  amber: 'Oddzwonić',
  green: 'Czeka na klienta',
  blue: 'Negocjacje',
  violet: 'Do sprawdzenia',
};
